<template>
  <div
    class="dropzone-wrap inline-block align-middle leading-none relative"
    :class="classObj"
  >
    <div
      ref="dropzone"
      class="dropzone text-xs absolute left-0 top-0 w-full h-full"
    />
    <div
      v-if="previewUrl"
      class="absolute left-0 top-0 w-full h-full pointer-events-none z-10"
    >
      <img
        class="object-cover object-center w-full h-full"
        :src="previewUrl"
        alt
      />
    </div>
  </div>
</template>

<script>
import wait from 'waait'
import uniqid from 'uniqid'
import Dropzone from 'dropzone'
import 'dropzone/dist/dropzone.css'
import { UPLOAD_ENDPOINT } from 'spencer'

Dropzone.autoDiscover = false

const isDev = process.env.NODE_ENV !== 'production'

export default {
  name: 'AdminDropzone',

  props: {
    baseUrl: {
      default: UPLOAD_ENDPOINT,
      type: String
    },

    basePath: {
      default: 'media',
      type: String
    },

    maxFilesize: {
      default: 5, // 5MB
      type: Number
    },

    fileType: {
      default: 'image',
      type: String
    },

    previewUrl: {
      default: null,
      type: String
    },

    restricted: {
      default: false,
      type: Boolean
    }
  },

  data() {
    return {
      fileName: null
    }
  },

  computed: {
    classObj() {
      return {
        'is-preview': this.previewUrl || this.previewText
      }
    },

    acceptedFiles() {
      let ft = this.fileType
      if (ft) {
        ft = `${ft}/*`
      }
      return ft
    },

    filePath() {
      const name = this.fileName
      return name ? `${this.basePath}/${name}` : null
    },

    url() {
      const path = this.filePath
      return path ? `${this.baseUrl}/${path}` : null
    }
  },

  beforeDestroy() {
    if (this.dropzone) {
      this.dropzone.destroy()
      this.dropzone = null
    }
  },

  mounted() {
    const vm = this
    const headers = isDev
      ? { 'Cache-Control': null, 'X-Requested-With': null }
      : {}
    this.dropzone = new Dropzone(this.$refs.dropzone, {
      url: '/', // changes for each new file being processed
      header: '', // changes for each new file being processed
      headers,
      maxFiles: 1,
      maxFilesize: this.maxFilesize,
      acceptedFiles: this.acceptedFiles,
      dictDefaultMessage: '',
      createImageThumbnails: false,
      method: 'put',
      // hijack the xhr.send since DZ always uploads files by using `formData`
      // ref: https://github.com/danialfarid/ng-file-upload/issues/743
      sending(file, xhr) {
        const _send = xhr.send
        xhr.send = () => {
          _send.call(xhr, file)
        }
      },
      // upload only 1 file at a time since we're using the s3 pre-signed url
      parallelUploads: 1,
      uploadMultiple: false,
      autoProcessQueue: false,
      // request a signed upload url when a file being accepted
      async accept(file, done) {
        const fileName = vm.getFileName(file.name)
        vm.fileName = fileName
        await wait()
        const filePath = vm.filePath
        const contentType = file.type
        const restricted = vm.restricted
        await fetch(UPLOAD_ENDPOINT, {
          method: 'POST',
          mode: 'no-cors',
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            filePath,
            contentType,
            restricted
          })
        })
          .then(res => res.json())
          .then(async ({ url }) => {
            file.uploadURL = url
            done()
            await wait()
            vm.dropzone.processFile(file)
          })
          .catch(err => {
            done('Failed to get an S3 signed upload URL', err)
          })
      }
    })
    this.dropzone.on('processing', file => {
      this.dropzone.options.url = file.uploadURL
    })
    this.dropzone.on('complete', async file => {
      let size
      let media = false
      await wait(2000) // wait for animation to complete
      const url = this.url
      const fileType = this.fileType
      const name = this.fileName
      const path = this.basePath
      try {
        if (fileType === 'image') {
          size = await this.getImageSize(url)
          media = true
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
      }
      if (media && size) {
        this.$emit('success', { url, size, name, path })
      } else if (url) {
        this.$emit('success', { url, name, path })
      } else {
        this.$emit('failure')
      }
      this.dropzone.removeFile(file)
    })
  },

  methods: {
    async getImageSize(url) {
      let image = new Image()
      const imageLoad = src => {
        return new Promise((resolve, reject) => {
          image.onload = () =>
            resolve({
              width: image.naturalWidth,
              height: image.naturalHeight
            })
          image.onerror = reject
          image.src = src
        })
      }
      const size = await imageLoad(url)
      image.src = ''
      image = null
      return size
    },

    getFileName(name) {
      // bust caching
      const hash = uniqid()
      return this.appendToFileName(name, hash)
    },

    appendToFileName(name, string) {
      const dotIndex = name.lastIndexOf('.')
      if (dotIndex === -1) {
        return `${name}-${string}`
      } else {
        return `${name.substring(0, dotIndex)}-${string}${name.substring(
          dotIndex
        )}`
      }
    }
  }
}
</script>

<style>
.dropzone-wrap {
  height: 1em;
  width: 1em;
}

.dropzone {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
  padding: 0;
  position: relative;
}

.is-error .dropzone {
  border: 1px solid red;
}

.is-preview .dz-message {
  display: none;
}

.dropzone .dz-preview,
.dropzone .dz-details {
  height: 100%;
  left: 0;
  margin: 0;
  min-height: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

.dropzone .dz-preview.dz-file-preview .dz-image {
  border-radius: 0;
  height: 100%;
  width: 100%;
}

.dropzone .dz-message {
  flex: 0 0 auto;
  margin: 0;
}
</style>
